import React from 'react';
import { LoadingAnimation } from '@sg/garnish';

import { NotFoundView } from '@order/components';
import { useDeliveryOrderInFlight } from '@order/hooks';

import { useProductDetailsDeliveryRestaurantQuery } from '../../GraphQL/ProductDetailsDeliveryRestaurant.generated';
import { CustomizationV2 } from './CustomizationV2';

// ─────────────────────────────────────────────────────────────────────────────

export const DeliveryProductDetailsV2 = (props: Props) => {
  const {
    addressId,
    productSlug,
    lineItemId,
    reorderLineItemId,
    isEditProductMode,
    onAddLineItemToCart,
    onEditLineItemInCart,
    onRemoveLineItemFromCart,
  } = props;

  // ─── Queries ─────────────────────────────────────────────────────

  const [{ fetching, data }] = useProductDetailsDeliveryRestaurantQuery({
    variables: { input: { addressId } },
    pause: !addressId,
  });

  // ─── Delivery Order In Flight ──────────────────────────────────────

  useDeliveryOrderInFlight({ pause: !addressId }); // results later used from the cache.

  // ─────────────────────────────────────────────────────────────────────

  if (fetching) {
    return <LoadingAnimation />;
  }

  if (data?.locationByAddress.__typename !== 'Location') {
    return <NotFoundView />;
  }

  // ─── Data ────────────────────────────────────────────────────────

  const { locationByAddress } = data;
  const { restaurant, vendor } = locationByAddress;
  const { deliveryFee, slug: restaurantSlug } = restaurant;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <CustomizationV2
      restaurantSlug={restaurantSlug}
      productSlug={productSlug}
      addressId={addressId}
      deliveryFee={deliveryFee}
      vendor={vendor}
      reorderLineItemId={reorderLineItemId}
      lineItemId={lineItemId}
      isEditProductMode={isEditProductMode}
      onAddLineItemToCart={onAddLineItemToCart}
      onEditLineItemInCart={onEditLineItemInCart}
      onRemoveLineItemFromCart={onRemoveLineItemFromCart}
    />
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  addressId: string;
  productSlug: string;
  lineItemId?: string;
  reorderLineItemId?: string;
  isEditProductMode: boolean;
  onAddLineItemToCart?: () => void;
  onEditLineItemInCart?: () => void;
  onRemoveLineItemFromCart?: () => void;
};
